import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { 
  LayoutDashboard, 
  Newspaper, 
  BookOpen, 
  Users, 
  LogOut,
  ArrowLeft,
  Save,
  Video,
  Play
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminVideoEditor = () => {
  const { id } = useParams();
  const isEditing = id && id !== 'new';
  const { admin, token, logout } = useAuth();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    author_name: '',
    video_url: '',
    video_type: 'youtube',
    duration: '',
    publication_date: new Date().toISOString().split('T')[0],
    published: false
  });
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    if (isEditing) {
      fetchVideo();
    }
  }, [id]);

  useEffect(() => {
    // Auto-detect video type and generate preview
    if (formData.video_url) {
      if (formData.video_url.includes('youtube.com') || formData.video_url.includes('youtu.be')) {
        setFormData(prev => ({ ...prev, video_type: 'youtube' }));
        const match = formData.video_url.match(/(?:youtube\.com\/(?:[^\/]+\/.+\/|(?:v|e(?:mbed)?)\/|.*[?&]v=)|youtu\.be\/)([^"&?\/\s]{11})/);
        if (match) {
          setPreviewUrl(`https://www.youtube.com/embed/${match[1]}`);
        }
      } else if (formData.video_url.includes('vimeo.com')) {
        setFormData(prev => ({ ...prev, video_type: 'vimeo' }));
        const match = formData.video_url.match(/vimeo\.com\/(\d+)/);
        if (match) {
          setPreviewUrl(`https://player.vimeo.com/video/${match[1]}`);
        }
      }
    } else {
      setPreviewUrl(null);
    }
  }, [formData.video_url]);

  const fetchVideo = async () => {
    try {
      const response = await fetch(`${API_URL}/api/videos/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setFormData({
          title: data.title,
          description: data.description || '',
          author_name: data.author_name,
          video_url: data.video_url,
          video_type: data.video_type || 'youtube',
          duration: data.duration || '',
          publication_date: data.publication_date ? data.publication_date.split('T')[0] : new Date().toISOString().split('T')[0],
          published: data.published
        });
      } else {
        toast.error('Vidéo non trouvée');
        navigate('/admin/videos');
      }
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const url = isEditing 
        ? `${API_URL}/api/videos/${id}` 
        : `${API_URL}/api/videos`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(isEditing ? 'Vidéo mise à jour' : 'Vidéo ajoutée');
        navigate('/admin/videos');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin/login');
  };

  const menuItems = [
    { icon: LayoutDashboard, label: 'Tableau de bord', path: '/admin' },
    { icon: Newspaper, label: 'Actualités', path: '/admin/news' },
    { icon: BookOpen, label: 'Livres', path: '/admin/books' },
    { icon: Video, label: 'Interviews', path: '/admin/videos', active: true },
    { icon: Users, label: 'Utilisateurs', path: '/admin/users' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-100 flex items-center justify-center">
        <p className="text-stone-600">Chargement...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-stone-900 text-white flex flex-col">
        <div className="p-6 border-b border-stone-700">
          <h1 className="text-xl font-serif font-bold">Quid Novi</h1>
          <p className="text-stone-400 text-sm mt-1">Administration</p>
        </div>

        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {menuItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded transition-colors ${
                    item.active 
                      ? 'bg-hermes-600 text-white' 
                      : 'text-stone-300 hover:bg-stone-800'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        <div className="p-4 border-t border-stone-700">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-stone-400 hover:text-white hover:bg-stone-800 rounded transition-colors"
          >
            <LogOut className="w-5 h-5" />
            <span>Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8">
        <div className="mb-8">
          <Link 
            to="/admin/videos"
            className="inline-flex items-center gap-2 text-stone-600 hover:text-hermes-600 transition-colors mb-4"
          >
            <ArrowLeft className="w-4 h-4" />
            Retour aux vidéos
          </Link>
          <h2 className="text-2xl font-serif font-bold text-stone-900">
            {isEditing ? 'Modifier la vidéo' : 'Nouvelle vidéo'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Form Fields */}
          <div className="bg-white shadow-sm p-6 space-y-6">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Titre de l'interview *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 focus:border-hermes-600 focus:ring-1 focus:ring-hermes-600 outline-none"
                placeholder="Ex: Interview de Jean Dupont sur son dernier livre"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Nom de l'auteur *
              </label>
              <input
                type="text"
                value={formData.author_name}
                onChange={(e) => setFormData({ ...formData, author_name: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 focus:border-hermes-600 focus:ring-1 focus:ring-hermes-600 outline-none"
                placeholder="Jean Dupont"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                URL de la vidéo (YouTube ou Vimeo) *
              </label>
              <input
                type="url"
                value={formData.video_url}
                onChange={(e) => setFormData({ ...formData, video_url: e.target.value })}
                className="w-full px-4 py-3 border border-stone-300 focus:border-hermes-600 focus:ring-1 focus:ring-hermes-600 outline-none"
                placeholder="https://www.youtube.com/watch?v=..."
                required
              />
              <p className="text-xs text-stone-500 mt-1">
                Collez l'URL YouTube ou Vimeo de la vidéo
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 border border-stone-300 focus:border-hermes-600 focus:ring-1 focus:ring-hermes-600 outline-none resize-none"
                placeholder="Description de l'interview..."
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Durée
                </label>
                <input
                  type="text"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-300 focus:border-hermes-600 focus:ring-1 focus:ring-hermes-600 outline-none"
                  placeholder="12:34"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">
                  Date de publication
                </label>
                <input
                  type="date"
                  value={formData.publication_date}
                  onChange={(e) => setFormData({ ...formData, publication_date: e.target.value })}
                  className="w-full px-4 py-3 border border-stone-300 focus:border-hermes-600 focus:ring-1 focus:ring-hermes-600 outline-none"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="published"
                checked={formData.published}
                onChange={(e) => setFormData({ ...formData, published: e.target.checked })}
                className="w-5 h-5 text-hermes-600 border-stone-300 rounded focus:ring-hermes-600"
              />
              <label htmlFor="published" className="text-sm font-medium text-stone-700">
                Publier immédiatement
              </label>
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={saving || !formData.title || !formData.author_name || !formData.video_url}
                className="flex items-center gap-2 px-6 py-3 bg-hermes-600 text-white font-medium hover:bg-hermes-700 transition-colors disabled:opacity-50"
              >
                <Save className="w-5 h-5" />
                {saving ? 'Enregistrement...' : 'Enregistrer'}
              </button>
              <Link
                to="/admin/videos"
                className="px-6 py-3 border border-stone-300 text-stone-700 hover:bg-stone-50 transition-colors"
              >
                Annuler
              </Link>
            </div>
          </div>

          {/* Video Preview */}
          <div className="bg-white shadow-sm p-6">
            <h3 className="text-lg font-medium text-stone-900 mb-4">Aperçu</h3>
            {previewUrl ? (
              <div className="aspect-video bg-stone-900 rounded overflow-hidden">
                <iframe
                  src={previewUrl}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Video preview"
                />
              </div>
            ) : (
              <div className="aspect-video bg-stone-100 rounded flex items-center justify-center">
                <div className="text-center text-stone-400">
                  <Video className="w-16 h-16 mx-auto mb-3" />
                  <p>Collez une URL YouTube ou Vimeo pour voir l'aperçu</p>
                </div>
              </div>
            )}

            {formData.video_type && (
              <p className="mt-4 text-sm text-stone-600">
                Type détecté : <span className="font-medium capitalize">{formData.video_type}</span>
              </p>
            )}
          </div>
        </form>
      </main>
    </div>
  );
};

export default AdminVideoEditor;
